import React, { useState } from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: 'How long does the visa processing take?',
      answer: 'Processing times vary depending on the visa type and destination country. Typically, tourist visas take 3-15 business days, student visas 4-8 weeks, and family visas 3-12 months. We offer express processing for urgent cases.',
      category: 'Processing'
    },
    {
      question: 'What documents do I need for a tourist visa?',
      answer: 'Generally, you need a valid passport, completed application form, passport-sized photos, proof of accommodation, round-trip flight tickets, travel insurance, and proof of sufficient funds. Specific requirements vary by country.',
      category: 'Requirements'
    },
    {
      question: 'Can you help if my visa application was previously rejected?',
      answer: 'Yes, we specialize in previously refused cases. Our experts analyze the refusal reasons, strengthen your application, address any concerns, and reapply with a higher chance of success.',
      category: 'Appeals'
    },
    {
      question: 'Do you offer visa services for families?',
      answer: 'Absolutely! We handle family visa applications including spouse, children, and parents. We ensure all family applications are coordinated properly to avoid delays or discrepancies.',
      category: 'Family'
    },
    {
      question: 'What is a Schengen visa and which countries does it cover?',
      answer: 'A Schengen visa allows travel to 26 European countries in the Schengen Area. This includes popular destinations like France, Germany, Italy, Spain, and many others with a single visa.',
      category: 'Schengen'
    },
    {
      question: 'How do I track my visa application status?',
      answer: 'We provide a dedicated client portal where you can track your application in real-time. You also get a personal consultant who provides regular updates via email and phone.',
      category: 'Tracking'
    },
    {
      question: 'Do I need to visit your office in person?',
      answer: 'No, we handle everything remotely. Through our online portal, video consultations, and secure document upload, we can manage your entire application without in-person visits.',
      category: 'Process'
    },
    {
      question: 'What is your success rate?',
      answer: 'We maintain a 95% success rate across all visa categories. Our thorough document verification, expert guidance, and attention to detail significantly increase approval chances.',
      category: 'Success'
    }
  ];

  const categories = ['All', 'Processing', 'Requirements', 'Family', 'Schengen', 'Appeals', 'Tracking', 'Success'];
  const [activeCategory, setActiveCategory] = useState('All');

  const filteredFaqs = activeCategory === 'All' 
    ? faqs 
    : faqs.filter(faq => faq.category === activeCategory);

  return (
    <section className="py-20 bg-white">
      <div className="container-padding">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Find answers to common questions about our visa services
          </p>
        </motion.div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-4 py-2 rounded-full font-medium transition ${
                activeCategory === category
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* FAQ Items */}
        <div className="max-w-4xl mx-auto">
          {filteredFaqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className="mb-4"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full text-left p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition flex justify-between items-center"
              >
                <div className="flex items-start">
                  <span className="inline-block w-8 h-8 bg-primary-100 text-primary-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    Q
                  </span>
                  <div>
                    <span className="inline-block px-3 py-1 text-xs font-medium bg-primary-100 text-primary-600 rounded-full mb-2">
                      {faq.category}
                    </span>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {faq.question}
                    </h3>
                  </div>
                </div>
                <div className="ml-4 flex-shrink-0">
                  {openIndex === index ? (
                    <FaChevronUp className="text-primary-600" />
                  ) : (
                    <FaChevronDown className="text-gray-400" />
                  )}
                </div>
              </button>
              
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-14 py-4 bg-gray-50 rounded-b-xl">
                      <div className="flex">
                        <span className="inline-block w-8 h-8 bg-green-100 text-green-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                          A
                        </span>
                        <p className="text-gray-700 pt-1">{faq.answer}</p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* Additional Help */}
        <div className="mt-16 text-center">
          <div className="inline-block bg-gradient-to-r from-primary-50 to-white rounded-2xl p-8 max-w-2xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Still have questions?
            </h3>
            <p className="text-gray-600 mb-6">
              Our visa experts are available 24/7 to answer your specific questions
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+15551234567"
                className="btn-primary"
              >
                Call Now: 0313-5993810
              </a>
              <a
                href="mailto:nextstepconsultant91@gmail.com"
                className="btn-secondary"
              >
                Email Us
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;